import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";

import Sidebare from "../Sidbare/Sidebare";
import Strip from "../../../components/Strip";
import DemandesAdmin from "../../../Services/Admin/DemandesAdmin";
import { DemandeType } from "../../../Modeles/DemandeModel";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";

export interface DemandeState {
  demande: DemandeType[];
  messageErros: string;
}

export default function DemandeAdmin() {
  const navigate = useNavigate();
  const [popup, setPopup] = useState<{
    visible: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({ visible: false, title: "", message: "", type: "success" });

  const [state, setState] = useState<DemandeState>({
    demande: [],
    messageErros: "Aucune demande",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [processingOverlay, setProcessingOverlay] = useState<{
    visible: boolean;
    label: string;
  }>({ visible: false, label: "" });

  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const itemsPerPage = 4;

  // Mobile detection
  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return isMobile;
  };
  const isMobile = useIsMobile();

  // Fetch demandes
  useEffect(() => {
    setLoading(true);
    DemandesAdmin()
      .GetCommande()
      .then((res) => {
        setState((prev) => ({ ...prev, demande: res.data }));
        setLoading(false);
      })
      .catch((err: any) => {
        setState((prev) => ({
          ...prev,
          messageErros: err.messageErros || "Erreur de récupération",
        }));
        setLoading(false);
      });
  }, []);

  const active = async (e: any, id: any) => {
    e.preventDefault();
    setProcessingOverlay({ visible: true, label: "Activation en cours..." });
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/demande/${id}/active`
      );

      setState((prev) => ({
        ...prev,
        demande: prev.demande.map((d) =>
          d.id === id ? { ...d, status: "active" } : d
        ),
      }));

      setPopup({
        visible: true,
        title: "Félicitations !",
        message: "Le magasin a été activé avec succès",
        type: "success",
      });

      setTimeout(() => setPopup({ ...popup, visible: false }), 3000);
    } catch (error: AxiosError | any) {
      setPopup({
        visible: true,
        title: "Erreur !",
        message: "Erreur lors de l'activation",
        type: "error",
      });
      setTimeout(() => setPopup({ ...popup, visible: false }), 3000);
    } finally {
      setProcessingOverlay({ visible: false, label: "" });
    }
  };

  const supprimer = async (e: any, id: any) => {
    e.preventDefault();
    setProcessingOverlay({ visible: true, label: "Suppression en cours..." });
    try {
      await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/demande/${id}/supprimer`
      );

      setState((prev) => ({
        ...prev,
        demande: prev.demande.filter((d) => d.id !== id),
      }));

      setPopup({
        visible: true,
        title: "Félicitations !",
        message: "Le magasin a été supprimé avec succès",
        type: "success",
      });

      setTimeout(() => setPopup({ ...popup, visible: false }), 3000);
    } catch (error: AxiosError | any) {
      setPopup({
        visible: true,
        title: "Erreur !",
        message: "Erreur lors de la suppression",
        type: "error",
      });
      setTimeout(() => setPopup({ ...popup, visible: false }), 3000);
    } finally {
      setProcessingOverlay({ visible: false, label: "" });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDateFilter(e.target.value);

  const filteredProducts = useMemo(() => {
    return state.demande.filter((p) => {
      const matchesSearch =
        p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.NomMagasin.toLowerCase().includes(searchTerm.toLowerCase());

      const today = new Date();
      const creationDate = new Date(p.date_creation);
      let matchesDate = false;

      switch (dateFilter) {
        case "Aujourd'hui":
          matchesDate = creationDate.toDateString() === today.toDateString();
          break;
        case "Hier":
          matchesDate =
            creationDate.toDateString() ===
            new Date(today.getTime() - 864e5).toDateString();
          break;
        case "Cette semaine":
          matchesDate =
            creationDate >= new Date(today.setDate(today.getDate() - 7));
          break;
        case "Ce mois":
          matchesDate = creationDate.getMonth() === new Date().getMonth();
          break;
        case "Cette année":
          matchesDate =
            creationDate.getFullYear() === new Date().getFullYear();
          break;
        default:
          matchesDate = true;
      }

      return matchesSearch && matchesDate;
    });
  }, [state.demande, searchTerm, dateFilter]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const pageNumbers = Array.from(
    { length: Math.ceil(filteredProducts.length / itemsPerPage) },
    (_, i) => i + 1
  );

  const handleChangePage = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return { color: "#059669", bg: "#d1fae5", icon: "check-circle-fill" };
      case "non active":
        return { color: "#dc2626", bg: "#fee2e2", icon: "x-circle-fill" };
      default:
        return { color: "#d97706", bg: "#fef3c7", icon: "clock-fill" };
    }
  };

  // Inline Styles
  const styles = {
    pageContainer: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: isMobile ? "20px 16px" : "40px 24px",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      animation: "slideUp 0.5s ease-out",
    },
    pageHeader: {
      marginBottom: "32px",
    },
    pageTitle: {
      fontSize: isMobile ? "1.75rem" : "2.25rem",
      fontWeight: 700,
      color: "#1a1a1a",
      margin: "0 0 8px 0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      letterSpacing: "-0.02em",
    },
    titleIcon: {
      fontSize: isMobile ? "1.5rem" : "2rem",
      color: "#ffc400",
    },
    pageSubtitle: {
      fontSize: "1rem",
      color: "#6b7280",
      margin: 0,
      fontWeight: 400,
    },
    filtersCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: isMobile ? "16px" : "24px",
      marginBottom: "24px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
    },
    filterGroup: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap" as const,
      flexDirection: isMobile ? ("column" as const) : ("row" as const),
    },
    searchContainer: {
      position: "relative" as const,
      flex: isMobile ? "1" : "2",
      minWidth: isMobile ? "100%" : "280px",
    },
    searchIcon: {
      position: "absolute" as const,
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280",
      fontSize: "1.1rem",
      pointerEvents: "none" as const,
    },
    searchInput: {
      width: "100%",
      padding: "14px 16px 14px 48px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "0.95rem",
      fontFamily: "'Outfit', sans-serif",
      fontWeight: 500,
      color: "#1a1a1a",
      background: "#ffffff",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxSizing: "border-box" as const,
    },
    selectContainer: {
      position: "relative" as const,
      flex: "1",
      minWidth: isMobile ? "100%" : "200px",
    },
    selectIcon: {
      position: "absolute" as const,
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280",
      fontSize: "1rem",
      pointerEvents: "none" as const,
      zIndex: 1,
    },
    select: {
      width: "100%",
      padding: "14px 16px 14px 48px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "0.95rem",
      fontFamily: "'Outfit', sans-serif",
      fontWeight: 500,
      color: "#1a1a1a",
      background: "#ffffff",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      appearance: "none" as const,
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236b7280' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 16px center",
      backgroundSize: "16px",
      boxSizing: "border-box" as const,
    },
    cardsGrid: {
      display: "grid",
      gap: "20px",
      marginBottom: "32px",
    },
    card: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "20px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      paddingBottom: "16px",
      borderBottom: "2px solid #f8f9fa",
    },
    orderId: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "1.1rem",
      fontWeight: 700,
      color: "#1a1a1a",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    statusBadge: (status: string) => {
      const config = getStatusConfig(status);
      return {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 14px",
        borderRadius: "100px",
        fontSize: "0.85rem",
        fontWeight: 600,
        textTransform: "capitalize" as const,
        color: config.color,
        backgroundColor: config.bg,
      };
    },
    cardBody: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "12px",
      marginBottom: "16px",
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
    },
    infoLabel: {
      fontSize: "0.9rem",
      color: "#6b7280",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    infoValue: {
      fontSize: "0.95rem",
      color: "#1a1a1a",
      fontWeight: 600,
      textAlign: "right" as const,
      wordBreak: "break-word" as const,
      maxWidth: "60%",
    },
    cardFooter: {
      paddingTop: "16px",
      borderTop: "2px solid #f8f9fa",
      display: "flex",
      gap: "10px",
    },
    actionButton: (type: "success" | "danger") => ({
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "12px 20px",
      border: "none",
      borderRadius: "12px",
      fontSize: "0.95rem",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: type === "success" ? "#059669" : "#dc2626",
      color: "#ffffff",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    }),
    tableCard: {
      background: "#ffffff",
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
      marginBottom: "32px",
    },
    table: {
      width: "100%",
      borderCollapse: "separate" as const,
      borderSpacing: 0,
    },
    tableHead: {
      background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
    },
    tableHeadCell: {
      padding: "18px 20px",
      textAlign: "left" as const,
      fontSize: "0.85rem",
      fontWeight: 600,
      color: "#ffffff",
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
      border: "none",
      whiteSpace: "nowrap" as const,
    },
    tableRow: {
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      borderBottom: "1px solid #e5e7eb",
    },
    tableCell: {
      padding: "18px 20px",
      fontSize: "0.95rem",
      color: "#1a1a1a",
      fontWeight: 500,
    },
    tableActionCell: {
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    tableButton: (type: "success" | "danger") => ({
      padding: "8px 16px",
      border: "none",
      borderRadius: "8px",
      fontSize: "0.9rem",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: type === "success" ? "#059669" : "#dc2626",
      color: "#ffffff",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    }),
    emptyState: {
      textAlign: "center" as const,
      padding: "80px 20px",
      background: "#ffffff",
      borderRadius: "16px",
      border: "2px dashed #e5e7eb",
    },
    emptyIcon: {
      fontSize: "4rem",
      color: "#6b7280",
      opacity: 0.3,
      marginBottom: "16px",
      display: "block",
    },
    emptyText: {
      fontSize: "1.1rem",
      color: "#6b7280",
      margin: 0,
      fontWeight: 500,
    },
    paginationContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "16px",
      padding: "20px 0",
      flexWrap: "wrap" as const,
      flexDirection: isMobile ? ("column" as const) : ("row" as const),
    },
    paginationButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 20px",
      background: "#ffffff",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      color: "#1a1a1a",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      fontSize: "0.95rem",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      width: isMobile ? "100%" : "auto",
      justifyContent: "center",
    },
    pageNumbers: {
      display: "flex",
      gap: "8px",
      width: isMobile ? "100%" : "auto",
      justifyContent: "center",
      flexWrap: "wrap" as const,
    },
    pageNumber: (isActive: boolean) => ({
      minWidth: "44px",
      height: "44px",
      padding: "0 12px",
      background: isActive ? "#ffc400" : "#ffffff",
      border: `2px solid ${isActive ? "#ffc400" : "#e5e7eb"}`,
      borderRadius: "8px",
      color: "#1a1a1a",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      fontSize: "0.95rem",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    popup: (type: "success" | "error") => ({
      position: "fixed" as const,
      top: "20px",
      right: "20px",
      zIndex: 9999,
      padding: "20px 24px",
      borderRadius: "16px",
      border: "1px solid #ffffff",
      color: "#ffffff",
      backgroundColor: type === "success" ? "#059669" : "#dc2626",
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
      minWidth: "300px",
      animation: "slideInRight 0.3s ease-out",
    }),
    popupTitle: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontFamily: "'Outfit', sans-serif",
      fontSize: "1.1rem",
      fontWeight: 700,
      margin: "0 0 8px 0",
    },
    popupMessage: {
      color:"white",
      fontFamily: "'Outfit', sans-serif",
      fontSize: "0.95rem",
      margin: 0,
      paddingLeft: "32px",
      opacity: 0.95,
    },
    loaderContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "70vh",
    },
    loaderContent: {
      textAlign: "center" as const,
    },
    spinner: {
      width: "60px",
      height: "60px",
      border: "4px solid #e5e7eb",
      borderTop: "4px solid #ffc400",
      borderRadius: "50%",
      animation: "spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite",
      margin: "0 auto 20px",
    },
    loaderText: {
      fontSize: "1rem",
      color: "#6b7280",
      fontWeight: 500,
      margin: 0,
    },
  };

  // Add keyframes styles
  const styleSheet = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes overlayFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes overlayCardIn {
      from { opacity: 0; transform: scale(0.85); }
      to { opacity: 1; transform: scale(1); }
    }
    
    input:focus, select:focus {
      outline: none !important;
      border-color: #ffc400 !important;
      box-shadow: 0 0 0 3px rgba(255, 196, 0, 0.1) !important;
    }
    
    button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
    
    button:active {
      transform: translateY(0);
    }
    
    table tbody tr:hover {
      background: rgba(255, 196, 0, 0.03);
    }
    
    .card-hover:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      border-color: #ffc400;
    }
  `;

  return (
    <>
      <style>{styleSheet}</style>
             <NewSideBarAdmin/>


      {loading ? (
        <div style={styles.loaderContainer}>
          <div style={styles.loaderContent}>
            <div style={styles.spinner}></div>
            <p style={styles.loaderText}>Chargement des données...</p>
          </div>
        </div>
      ) : (
        <div style={styles.pageContainer}>
          {/* Page Header */}
          <div style={styles.pageHeader}>
            <h1 style={styles.pageTitle}>
              <i className="bi bi-inbox" style={styles.titleIcon}></i>
              Demandes de magasins
            </h1>
            <p style={styles.pageSubtitle}>
              Gérez les nouvelles demandes d'inscription de magasins
            </p>
          </div>

          {/* Filters */}
          <div style={styles.filtersCard}>
            <div style={styles.filterGroup}>
              <div style={styles.searchContainer}>
                <i className="bi bi-search" style={styles.searchIcon}></i>
                <input
                  type="text"
                  placeholder="Rechercher par nom ou magasin..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={styles.searchInput}
                />
              </div>
              <div style={styles.selectContainer}>
                <i className="bi bi-calendar-event" style={styles.selectIcon}></i>
                <select
                  onChange={handleDateFilterChange}
                  value={dateFilter}
                  style={styles.select}
                >
                  <option value="">Toutes les dates</option>
                  <option value="Aujourd'hui">Aujourd'hui</option>
                  <option value="Hier">Hier</option>
                  <option value="Cette semaine">Cette semaine</option>
                  <option value="Ce mois">Ce mois</option>
                  <option value="Cette année">Cette année</option>
                </select>
              </div>
            </div>
          </div>

          {/* Mobile Cards */}
          {isMobile ? (
            <div style={styles.cardsGrid}>
              {currentItems.length > 0 ? (
                currentItems.map((pro) => {
                  const statusConfig = getStatusConfig(pro.status);
                  return (
                    <div key={pro.id} style={styles.card} className="card-hover">
                      <div style={styles.cardHeader}>
                        <div style={styles.orderId}>
                          <i className="bi bi-hash"></i>
                          {pro.id}
                        </div>
                        <div style={styles.statusBadge(pro.status)}>
                          <i className={`bi bi-${statusConfig.icon}`}></i>
                          {pro.status}
                        </div>
                      </div>

                      <div style={styles.cardBody}>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>
                            <i className="bi bi-person"></i>Nom
                          </span>
                          <span style={styles.infoValue}>{pro.nom}</span>
                        </div>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>
                            <i className="bi bi-shop"></i>Magasin
                          </span>
                          <span style={styles.infoValue}>{pro.NomMagasin}</span>
                        </div>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>
                            <i className="bi bi-geo-alt"></i>Adresse
                          </span>
                          <span style={styles.infoValue}>{pro.adress}</span>
                        </div>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>
                            <i className="bi bi-envelope"></i>Email
                          </span>
                          <span style={styles.infoValue}>{pro.email}</span>
                        </div>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>
                            <i className="bi bi-calendar-event"></i>Date
                          </span>
                          <span style={styles.infoValue}>
                            {pro.date_creation}
                          </span>
                        </div>
                      </div>

                      <div style={styles.cardFooter}>
                        <button
                          style={{ ...styles.actionButton("success"), opacity: processingOverlay.visible ? 0.6 : 1 }}
                          onClick={(e) => active(e, pro.id)}
                          disabled={processingOverlay.visible}
                        >
                          <i className="bi bi-check-circle"></i>
                          Activer
                        </button>
                        <button
                          style={{ ...styles.actionButton("danger"), opacity: processingOverlay.visible ? 0.6 : 1 }}
                          onClick={(e) => supprimer(e, pro.id)}
                          disabled={processingOverlay.visible}
                        >
                          <i className="bi bi-trash"></i>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={styles.emptyState}>
                  <i className="bi bi-inbox" style={styles.emptyIcon}></i>
                  <p style={styles.emptyText}>Aucune demande trouvée</p>
                </div>
              )}
            </div>
          ) : (
            // Desktop Table
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead style={styles.tableHead}>
                  <tr>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-hash"></i> ID
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-person"></i> Nom
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-shop"></i> Nom magasin
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-geo-alt"></i> Adresse
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-envelope"></i> Email
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-key"></i> Mot de passe
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-flag"></i> Statut
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-calendar-event"></i> Date
                    </th>
                    <th style={styles.tableHeadCell}>
                      <i className="bi bi-gear"></i> Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((pro) => {
                      const statusConfig = getStatusConfig(pro.status);
                      return (
                        <tr key={pro.id} style={styles.tableRow}>
                          <td style={styles.tableCell}>#{pro.id}</td>
                          <td style={styles.tableCell}>{pro.nom}</td>
                          <td style={styles.tableCell}>{pro.NomMagasin}</td>
                          <td style={styles.tableCell}>{pro.adress}</td>
                          <td style={styles.tableCell}>{pro.email}</td>
                          <td style={styles.tableCell}>{pro.password}</td>
                          <td style={styles.tableCell}>
                            <span style={styles.statusBadge(pro.status)}>
                              <i className={`bi bi-${statusConfig.icon}`}></i>
                              {pro.status}
                            </span>
                          </td>
                          <td style={styles.tableCell}>{pro.date_creation}</td>
                          <td style={styles.tableActionCell}>
                            <button
                              style={{ ...styles.tableButton("success"), opacity: processingOverlay.visible ? 0.6 : 1 }}
                              onClick={(e) => active(e, pro.id)}
                              disabled={processingOverlay.visible}
                            >
                              <i className="bi bi-check-circle"></i> Activer
                            </button>
                            <button
                              style={{ ...styles.tableButton("danger"), opacity: processingOverlay.visible ? 0.6 : 1 }}
                              onClick={(e) => supprimer(e, pro.id)}
                              disabled={processingOverlay.visible}
                            >
                              <i className="bi bi-trash"></i> Supprimer
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} style={styles.emptyState}>
                        <i className="bi bi-inbox" style={styles.emptyIcon}></i>
                        <span style={styles.emptyText}>
                          Aucune demande trouvée
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pageNumbers.length > 1 && (
            <div style={styles.paginationContainer}>
              <button
                style={styles.paginationButton}
                onClick={() => handleChangePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
                <span>Précédent</span>
              </button>

              <div style={styles.pageNumbers}>
                {pageNumbers.map((number) => (
                  <button
                    key={number}
                    style={styles.pageNumber(currentPage === number)}
                    onClick={() => handleChangePage(number)}
                  >
                    {number}
                  </button>
                ))}
              </div>

              <button
                style={styles.paginationButton}
                onClick={() => handleChangePage(currentPage + 1)}
                disabled={currentPage === pageNumbers.length}
              >
                <span>Suivant</span>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}

          {/* Popup Notification */}
          {popup.visible && (
            <div style={styles.popup(popup.type)}>
              <h5 style={styles.popupTitle}>
                <i className="bi bi-check-circle-fill"></i>
                {popup.title}
              </h5>
              <p style={styles.popupMessage}>{popup.message}</p>
            </div>
          )}
        </div>
      )}

      {/* Processing Overlay */}
      {processingOverlay.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "overlayFadeIn 0.2s ease-out",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              padding: "40px 48px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
              minWidth: "260px",
              animation: "overlayCardIn 0.25s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                border: "5px solid #e5e7eb",
                borderTop: "5px solid #ffc400",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "1.05rem",
                fontWeight: 600,
                color: "#1a1a1a",
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: "-0.01em",
              }}
            >
              {processingOverlay.label}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "0.85rem",
                color: "#6b7280",
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              Veuillez patienter...
            </p>
          </div>
        </div>
      )}
    </>
  );
}